import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { Resident, PaymentPeriod } from '../types';

type PaymentCategory = 'DP' | 'Pelunasan' | 'Cicilan';

interface EditPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  resident: Resident | null;
  period: PaymentPeriod | null;
  onSave: (
    resident: Resident,
    period: PaymentPeriod,
    updates: {
      paymentProofDate?: string;
      paymentProofLink?: string;
      category?: PaymentCategory;
      installmentNumber?: number;
  periodNumber?: number;
    }
  ) => Promise<boolean>;
}

export function EditPaymentModal({ isOpen, onClose, resident, period, onSave }: EditPaymentModalProps) {
  const [paymentProofDate, setPaymentProofDate] = useState('');
  const [paymentProofLink, setPaymentProofLink] = useState('');
  const [category, setCategory] = useState<PaymentCategory | ''>('');
  const [installmentNumber, setInstallmentNumber] = useState<number | ''>('');
  const [periodNumber, setPeriodNumber] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (period) {
      setPaymentProofDate(period.paymentProofDate || '');
      setPaymentProofLink(period.paymentProofLink || '');
      setCategory((period as any).category || '');
      setInstallmentNumber((period as any).installmentNumber || '');
  setPeriodNumber(period.periodNumber || '');
      setError('');
    }
  }, [period]);

  if (!isOpen || !resident || !period) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!paymentProofDate) { setError('Tanggal bukti bayar wajib.'); return; }
    if (category === 'Cicilan' && (!installmentNumber || Number(installmentNumber) < 1)) { setError('Nomor cicilan harus diisi dan >= 1.'); return; }
    setIsSubmitting(true);
    try {
      const ok = await onSave(resident, period, {
        paymentProofDate,
        paymentProofLink,
        category: category || undefined,
        installmentNumber: category === 'Cicilan' ? Number(installmentNumber) : undefined,
        periodNumber: periodNumber ? Number(periodNumber) : undefined
      });
      if (ok) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h3 className="font-semibold">Edit Pembayaran - {resident.name} (Periode {period.periodNumber})</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm mb-1">Kategori</label>
            <select value={category} onChange={e=>setCategory(e.target.value as PaymentCategory)} className="w-full p-2 border rounded bg-white dark:bg-gray-800">
              <option value="">-- Pilih Kategori --</option>
              <option value="DP">DP</option>
              <option value="Cicilan">Cicilan</option>
              <option value="Pelunasan">Pelunasan</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Periode Ke</label>
            <input type="number" min={1} value={periodNumber} onChange={e=>setPeriodNumber(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border rounded bg-white dark:bg-gray-800" />
          </div>
          {category==='Cicilan' && (
            <div>
              <label className="block text-sm mb-1">Cicilan ke-</label>
              <input type="number" min={1} value={installmentNumber} onChange={e=>setInstallmentNumber(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border rounded bg-white dark:bg-gray-800" />
            </div>
          )}
          <div>
            <label className="block text-sm mb-1">Tanggal Bukti Bayar</label>
            <div className="flex gap-2 items-center">
              <input type="date" value={paymentProofDate} onChange={e=>setPaymentProofDate(e.target.value)} className="flex-1 p-2 border rounded bg-white dark:bg-gray-800" required />
              <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{/* formatted */}{new Date(paymentProofDate || '').toString() === 'Invalid Date' ? '-' : (function(d){ const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')} ${String(dt.getMonth()+1).padStart(2,'0')} ${dt.getFullYear()}` })(paymentProofDate)}</div>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1">Link Bukti Bayar</label>
            <input type="url" value={paymentProofLink} onChange={e=>setPaymentProofLink(e.target.value)} className="w-full p-2 border rounded bg-white dark:bg-gray-800" placeholder="https://contoh.com/bukti.jpg" />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 border rounded">Batal</button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50">{isSubmitting ? 'Menyimpan...' : 'Simpan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditPaymentModal;
